import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Star, ArrowRight, Video, MessageCircle, Stethoscope } from 'lucide-react';
import suggestionService from '../services/suggestionService';
import ServiceCards from '../components/ServiceCards';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import HealthcareServicesCarousel from '../components/herosection';
import DoctorInfo from '../components/DoctorInfo';
import { MedicineCard } from '../components/orders/Medecine/MedicineCard';
import { useCart } from '../context/CartContext';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const API_BASE_URL = 'https://www.homecureexpress.com';

// Helper function to get full image URL
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  // If it's already a full URL, return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  // If it's a relative URL, prepend the base URL
  return `${API_BASE_URL}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
};

export default function Dashboard() {
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchCart, isAuthenticated } = useCart();

  useEffect(() => {
    let isMounted = true;

    const fetchSuggestions = async () => {
      try {
        setLoading(true);
        const data = await suggestionService.getSuggestions();
        if (isMounted && data.success) {
          setSuggestions(data);
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Fetch cart when dashboard loads (if user is logged in)
    if (isAuthenticated()) {
      fetchCart();
    }

    fetchSuggestions();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.key]); // Fetch on route change and navigation

  return (
    <>
      <Header />
      <HealthcareServicesCarousel />
      <ServiceCards />

      <div style={{ backgroundColor: '#f8f9fa', paddingBottom: '40px', padding: '0 4px' }}>
        {/* Popular Doctors */}
        {(loading || suggestions?.popular_doctors?.length > 0) && (
          <section style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <h2 style={sectionTitleStyle}>Top Doctors</h2>
                <p style={sectionSubtitleStyle}>Consult with verified & experienced doctors</p>
              </div>
              <Link to="/online-consultation" style={viewAllLinkStyle} onClick={() => window.scrollTo(0, 0)}>
                View All <ArrowRight size={16} />
              </Link>
            </div>

            <div style={doctorGridStyle}>
              {loading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} style={doctorCardStyle}>
                      <Skeleton height={280} />
                    </div>
                  ))
                : suggestions?.popular_doctors?.slice(0, 6).map((doctor) => (
                    <div key={doctor.id} style={doctorCardStyle} onClick={() => navigate(`/doctor/${doctor.id}`)}>
                      <div style={doctorImageContainerStyle}>
                        <img
                          src={getImageUrl(doctor.profile_image) || 'https://via.placeholder.com/400x300/104a5f/ffffff?text=Doctor'}
                          alt={doctor.name}
                          style={doctorImageStyle}
                          onError={(e) => {
                            if (!e.target.dataset.errored) {
                              e.target.dataset.errored = 'true';
                              e.target.src = 'https://via.placeholder.com/400x300/104a5f/ffffff?text=Doctor';
                            }
                          }}
                        />
                        {doctor.is_available_online && (
                          <span style={onlineBadgeStyle}>Online</span>
                        )}
                      </div>
                      <div style={doctorInfoStyle}>
                        <h3 style={doctorNameStyle}>{doctor.name}</h3>
                        <p style={doctorSpecStyle}>{doctor.specialization}</p>
                        <div style={doctorStatsStyle}>
                          <div style={ratingContainerStyle}>
                            <Star size={14} fill="#ffc107" color="#ffc107" />
                            <span>{parseFloat(doctor.average_rating) > 0 ? parseFloat(doctor.average_rating).toFixed(1) : '5.0'}</span>
                            <span style={reviewsCountStyle}>({doctor.total_reviews || 0})</span>
                          </div>
                          {doctor.experience_years > 0 && (
                            <span style={experienceStyle}>{doctor.experience_years}+ yrs</span>
                          )}
                        </div>
                        <div style={doctorActionsStyle}>
                          <span style={priceStyle}>₹{doctor.price}</span>
                          <div style={actionButtonsStyle}>
                            {doctor.video_call_enabled && (
                              <button style={iconButtonStyle} title="Video Consultation">
                                <Video size={16} />
                              </button>
                            )}
                            <button style={iconButtonStyle} title="Chat Consultation">
                              <MessageCircle size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
            </div>
          </section>
        )}

        {/* Popular Medicines */}
        {(loading || suggestions?.popular_medicines?.length > 0) && (
          <section style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <h2 style={sectionTitleStyle}>Popular Medicines</h2>
                <p style={sectionSubtitleStyle}>Order medicines with prescription upload</p>
              </div>
              <Link to="/Medicines" style={viewAllLinkStyle} onClick={() => window.scrollTo(0, 0)}>
                View All <ArrowRight size={16} />
              </Link>
            </div>

            <div style={medicineGridStyle}>
              {loading
                ? Array.from({ length: 4 }).map((_, i) => (
                    <div key={i}>
                      <Skeleton height={320} />
                    </div>
                  ))
                : suggestions?.popular_medicines?.slice(0, 8).map((medicine) => (
                    <MedicineCard key={medicine.id} item={medicine} />
                  ))}
            </div>
          </section>
        )}

        {/* Popular Services */}
        {(loading || suggestions?.popular_services?.length > 0) && (
          <section style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <h2 style={sectionTitleStyle}>Healthcare Services</h2>
                <p style={sectionSubtitleStyle}>Professional care at your doorstep</p>
              </div>
              <Link to="/paramedical" style={viewAllLinkStyle} onClick={() => window.scrollTo(0, 0)}>
                View All <ArrowRight size={16} />
              </Link>
            </div>

            <div style={serviceGridStyle}>
              {loading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} style={serviceCardStyle}>
                      <Skeleton height={240} />
                    </div>
                  ))
                : suggestions?.popular_services?.slice(0, 6).map((service) => (
                    <div key={service.id} style={serviceCardStyle}>
                      <div style={serviceImageContainerStyle}>
                        <img
                          src={getImageUrl(service.image) || 'https://via.placeholder.com/400x300/104a5f/ffffff?text=Service'}
                          alt={service.title}
                          style={serviceImageStyle}
                          onError={(e) => {
                            if (!e.target.dataset.errored) {
                              e.target.dataset.errored = 'true';
                              e.target.src = 'https://via.placeholder.com/400x300/104a5f/ffffff?text=Service';
                            }
                          }}
                        />
                      </div>
                      <div style={serviceInfoStyle}>
                        <h4 style={serviceNameStyle}>{service.title}</h4>
                        <p style={serviceDescStyle}>{service.description}</p>
                        <div style={servicePriceRowStyle}>
                          <div>
                            {service.offer_price && service.offer_price !== service.price ? (
                              <>
                                <span style={servicePriceStyle}>₹{service.offer_price}</span>
                                <span style={{ ...serviceDurationStyle, textDecoration: 'line-through', marginLeft: '8px' }}>
                                  ₹{service.price}
                                </span>
                              </>
                            ) : (
                              <span style={servicePriceStyle}>₹{service.price}</span>
                            )}
                          </div>
                          <button
                            style={bookButtonStyle}
                            onClick={() => navigate(`/paramedical/${service.id}`)}
                          >
                            Book Now
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
            </div>
          </section>
        )}

        {/* Popular Tests */}
        {(loading || suggestions?.popular_tests?.length > 0) && (
          <section style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <h2 style={sectionTitleStyle}>Popular Lab Tests</h2>
                <p style={sectionSubtitleStyle}>Book diagnostic tests from home</p>
              </div>
              <Link to="/medical-tests" style={viewAllLinkStyle} onClick={() => window.scrollTo(0, 0)}>
                View All <ArrowRight size={16} />
              </Link>
            </div>

            <div style={testGridStyle}>
              {loading
                ? Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} style={testCardStyle}>
                      <Skeleton height={220} />
                    </div>
                  ))
                : suggestions?.popular_tests?.slice(0, 8).map((test) => (
                    <div key={test.id} style={testCardStyle}>
                      <div style={testHeaderStyle}>
                        <div style={testIconStyle}>🔬</div>
                        {test.discount_percentage > 0 && (
                          <span style={testDiscountBadgeStyle}>{Math.round(test.discount_percentage)}% OFF</span>
                        )}
                      </div>
                      <div style={testInfoStyle}>
                        <h4 style={testNameStyle}>{test.title}</h4>
                        <p style={testDescStyle}>{test.description}</p>
                        <div style={testPriceRowStyle}>
                          <div>
                            {test.offer_price && parseFloat(test.offer_price) < parseFloat(test.price) ? (
                              <>
                                <span style={testPriceStyle}>₹{test.offer_price}</span>
                                <span style={testOriginalPriceStyle}>₹{test.price}</span>
                              </>
                            ) : (
                              <span style={testPriceStyle}>₹{test.price}</span>
                            )}
                          </div>
                          <button
                            style={bookButtonStyle}
                            onClick={() => navigate(`/medical-test/${test.id}`)}
                          >
                            Book Test
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
            </div>
          </section>
        )}

        {/* Popular Test Packages */}
        {(loading || suggestions?.popular_test_packages?.length > 0) && (
          <section style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <h2 style={sectionTitleStyle}>Health Checkup Packages</h2>
                <p style={sectionSubtitleStyle}>Comprehensive health screening at best prices</p>
              </div>
              <Link to="/medical-tests" style={viewAllLinkStyle} onClick={() => window.scrollTo(0, 0)}>
                View All <ArrowRight size={16} />
              </Link>
            </div>

            <div style={packageGridStyle}>
              {loading
                ? Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} style={packageCardStyle}>
                      <Skeleton height={260} />
                    </div>
                  ))
                : suggestions?.popular_test_packages?.slice(0, 4).map((pkg) => (
                    <div key={pkg.id} style={packageCardStyle}>
                      <div style={packageImageContainerStyle}>
                        <img
                          src={pkg.image ? getImageUrl(pkg.image) : 'https://via.placeholder.com/400x300/104a5f/ffffff?text=Health+Package'}
                          alt={pkg.name || 'Health Package'}
                          style={packageImageStyle}
                          onError={(e) => {
                            if (!e.target.dataset.errored) {
                              e.target.dataset.errored = 'true';
                              e.target.src = 'https://via.placeholder.com/400x300/104a5f/ffffff?text=Health+Package';
                            }
                          }}
                        />
                        {pkg.discount_percentage && (
                          <span style={discountBadgeStyle}>{Math.round(pkg.discount_percentage)}% OFF</span>
                        )}
                      </div>
                      <div style={packageInfoStyle}>
                        <h4 style={packageNameStyle}>{pkg.name}</h4>
                        <p style={packageDescStyle}>{pkg.description}</p>
                        <div style={packageTestsStyle}>
                          <Stethoscope size={14} color="#104a5f" />
                          <span>{pkg.test_count} Tests included</span>
                        </div>
                        <div style={packagePriceRowStyle}>
                          <div>
                            <span style={packagePriceStyle}>₹{pkg.effective_price || pkg.offer_price}</span>
                            {pkg.total_price && (
                              <span style={packageOriginalPriceStyle}>₹{pkg.total_price}</span>
                            )}
                          </div>
                          <button
                            style={bookButtonStyle}
                            onClick={() => navigate(`/test-package/${pkg.id}`)}
                          >
                            Book Now
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
            </div>
          </section>
        )}

        {/* Doctor Info Section */}
        <div style={{ maxWidth: '100%', padding: '0 4px' }}>
          <DoctorInfo />
        </div>
      </div>

      <Footer />
    </>
  );
}

// Styles
const sectionStyle = {
  maxWidth: '1400px',
  margin: '0 auto',
  padding: '40px 4px'
};

const sectionHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px'
};

const sectionTitleStyle = {
  fontSize: '28px',
  fontWeight: '700',
  color: '#104a5f',
  marginBottom: '4px'
};

const sectionSubtitleStyle = {
  fontSize: '14px',
  color: '#666',
  marginTop: '4px'
};

const viewAllLinkStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  color: '#104a5f',
  textDecoration: 'none',
  fontSize: '14px',
  fontWeight: '600',
  transition: 'gap 0.3s ease'
};

// Doctor Card Styles
const doctorGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: '20px'
};

const doctorCardStyle = {
  background: '#fff',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  cursor: 'pointer'
};

const doctorImageContainerStyle = {
  position: 'relative',
  width: '100%',
  height: '200px',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #104a5f 0%, #1a6b82 100%)'
};

const doctorImageStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'cover'
};

const onlineBadgeStyle = {
  position: 'absolute',
  top: '12px',
  right: '12px',
  background: '#10b981',
  color: '#fff',
  padding: '4px 12px',
  borderRadius: '20px',
  fontSize: '12px',
  fontWeight: '600'
};

const doctorInfoStyle = {
  padding: '16px'
};

const doctorNameStyle = {
  fontSize: '18px',
  fontWeight: '600',
  color: '#333',
  marginBottom: '4px'
};

const doctorSpecStyle = {
  fontSize: '14px',
  color: '#666',
  marginBottom: '12px'
};

const doctorStatsStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '12px',
  paddingBottom: '12px',
  borderBottom: '1px solid #f0f0f0'
};

const ratingContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '14px',
  fontWeight: '600',
  color: '#333'
};

const reviewsCountStyle = {
  fontSize: '12px',
  color: '#999'
};

const experienceStyle = {
  fontSize: '12px',
  color: '#666',
  background: '#f8f9fa',
  padding: '4px 8px',
  borderRadius: '4px'
};

const doctorActionsStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const priceStyle = {
  fontSize: '20px',
  fontWeight: '700',
  color: '#104a5f'
};

const actionButtonsStyle = {
  display: 'flex',
  gap: '8px'
};

const iconButtonStyle = {
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  border: '1px solid #e0e0e0',
  background: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  color: '#104a5f'
};

// Medicine Card Styles
const medicineGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: '16px'
};

// Service Card Styles
const serviceGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: '20px'
};

const serviceCardStyle = {
  background: '#fff',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
};

const serviceImageContainerStyle = {
  width: '100%',
  height: '160px',
  overflow: 'hidden'
};

const serviceImageStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'cover'
};

const serviceInfoStyle = {
  padding: '16px'
};

const serviceNameStyle = {
  fontSize: '16px',
  fontWeight: '600',
  color: '#333',
  marginBottom: '8px'
};

const serviceDescStyle = {
  fontSize: '13px',
  color: '#666',
  marginBottom: '12px',
  lineHeight: '1.4',
  height: '36px',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical'
};

const servicePriceRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const servicePriceStyle = {
  fontSize: '18px',
  fontWeight: '700',
  color: '#104a5f'
};

const serviceDurationStyle = {
  fontSize: '12px',
  color: '#666'
};

const bookButtonStyle = {
  background: '#104a5f',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '8px 16px',
  fontSize: '13px',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'background 0.3s ease'
};

// Package Card Styles
const packageGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  gap: '20px'
};

const packageCardStyle = {
  background: '#fff',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
};

const packageImageContainerStyle = {
  position: 'relative',
  width: '100%',
  height: '180px',
  overflow: 'hidden'
};

const packageImageStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'cover'
};

const discountBadgeStyle = {
  position: 'absolute',
  top: '12px',
  left: '12px',
  background: '#10b981',
  color: '#fff',
  padding: '6px 12px',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '700'
};

const packageInfoStyle = {
  padding: '16px'
};

const packageNameStyle = {
  fontSize: '18px',
  fontWeight: '600',
  color: '#333',
  marginBottom: '8px'
};

const packageDescStyle = {
  fontSize: '13px',
  color: '#666',
  marginBottom: '12px',
  lineHeight: '1.4',
  height: '36px',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical'
};

const packageTestsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  marginBottom: '12px',
  fontSize: '13px',
  color: '#666'
};

const packagePriceRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const packagePriceStyle = {
  fontSize: '20px',
  fontWeight: '700',
  color: '#104a5f',
  marginRight: '8px'
};

const packageOriginalPriceStyle = {
  fontSize: '14px',
  color: '#999',
  textDecoration: 'line-through'
};

// Test Card Styles
const testGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  gap: '20px'
};

const testCardStyle = {
  background: '#fff',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  cursor: 'pointer'
};

const testHeaderStyle = {
  position: 'relative',
  padding: '16px',
  background: 'linear-gradient(135deg, #e0f2fe 0%, #ddd6fe 100%)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const testIconStyle = {
  fontSize: '32px'
};

const testDiscountBadgeStyle = {
  background: '#10b981',
  color: '#fff',
  padding: '4px 8px',
  borderRadius: '6px',
  fontSize: '12px',
  fontWeight: '700'
};

const testInfoStyle = {
  padding: '16px'
};

const testNameStyle = {
  fontSize: '16px',
  fontWeight: '600',
  color: '#333',
  marginBottom: '8px'
};

const testDescStyle = {
  fontSize: '13px',
  color: '#666',
  marginBottom: '12px',
  lineHeight: '1.4',
  height: '54px',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical'
};

const testPriceRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingTop: '12px',
  borderTop: '1px solid #f0f0f0'
};

const testPriceStyle = {
  fontSize: '18px',
  fontWeight: '700',
  color: '#104a5f',
  marginRight: '8px'
};

const testOriginalPriceStyle = {
  fontSize: '14px',
  color: '#999',
  textDecoration: 'line-through',
  marginLeft: '4px'
};
